import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, money, errMsg } from "@/lib/api";
import { RecordedSave } from "@/admin/Recorded";
import { AdminTitle, Btn, Pill, Table } from "@/admin/AdminLayout";
import { STATUS_LABEL } from "@/pages/Account";
import { inputCls } from "@/components/common";
import { BankSettings } from "@/admin/BankSettings";

const STATUSES = ["pending_payment", "confirmed", "in_production", "shipped", "delivered", "cancelled"];

export default function AdminOrders() {
  const [orders, setOrders] = useState<any[]>([]);
  const [edit, setEdit] = useState<any>(null);
  const [form, setForm] = useState<any>({});
  const [filter, setFilter] = useState("paid");
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const open = (o: any) => { setEdit(o); setForm({ status: o.status, note: "", carrier: o.tracking?.carrier || "", tracking_number: o.tracking?.number || "", tracking_url: o.tracking?.url || "" }); };
  const save = async () => {
    try { await api.put(`/admin/orders/${edit.id}`, form); toast("Order updated — customer notified by email where applicable"); load(); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const markPaid = async (o: any) => {
    if (!window.confirm(`Confirm the bank transfer of ${money(o.total)} for ${o.number} has arrived?`)) return;
    try { await api.post(`/admin/orders/${o.id}/mark-paid`); toast(`${o.number} marked as paid — confirmation emailed`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const shown = orders.filter((o) => filter === "all" || (filter === "transfer" ? o.payment_status === "awaiting_transfer" : filter === "paid" ? o.payment_status === "paid" : o.payment_status !== "paid"));

  return (
    <div data-testid="admin-orders">
      <AdminTitle title="Orders" sub={`${shown.length} orders`}>
        <BankSettings />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border border-neutral-300 bg-white px-3 py-2 text-sm" data-testid="orders-filter">
          <option value="paid">Paid</option><option value="transfer">Awaiting bank transfer</option><option value="unpaid">Unpaid / abandoned</option><option value="all">All</option>
        </select>
      </AdminTitle>
      <Table head={["Order", "Date", "Customer", "Items", "Total", "Payment", "Status", ""]} testId="orders-table">
        {shown.map((o) => (
          <tr key={o.id} data-testid={`admin-order-row-${o.number}`}>
            <td className="px-4 py-3 font-medium">{o.number}{o.financing_interest && <Pill tone="amber">financing</Pill>}</td>
            <td className="px-4 py-3 text-neutral-500">{new Date(o.created_at).toLocaleDateString("en-AU")}</td>
            <td className="px-4 py-3">{o.name}<br /><span className="text-xs text-neutral-500">{o.email}</span></td>
            <td className="px-4 py-3 text-xs">{o.items.map((i: any) => `${i.name} ×${i.qty}`).join(", ")}</td>
            <td className="px-4 py-3">{money(o.total)}{o.promo_code && <div className="text-[11px] text-neutral-500">{o.promo_code}</div>}</td>
            <td className="px-4 py-3"><Pill tone={o.payment_status === "paid" ? "green" : o.payment_status === "cancelled" ? "red" : "amber"}>{o.payment_status === "awaiting_transfer" ? "awaiting transfer" : o.payment_status}</Pill>{o.provider === "bank_transfer" && <div className="text-[11px] text-neutral-500 mt-1">Bank transfer</div>}</td>
            <td className="px-4 py-3">{STATUS_LABEL[o.status]}</td>
            <td className="px-4 py-3 space-y-2">{o.payment_status === "awaiting_transfer" && o.status !== "cancelled" && <Btn onClick={() => markPaid(o)} data-testid={`admin-order-mark-paid-${o.number}`}>Mark as paid</Btn>}
              <Btn ghost onClick={() => open(o)} data-testid={`admin-order-edit-${o.number}`}>Update</Btn></td>
          </tr>
        ))}
      </Table>
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="bg-white" data-testid="order-edit-dialog">
          <DialogTitle className="font-display text-2xl">Update {edit?.number}</DialogTitle>
          <div className="space-y-4">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls} data-testid="order-status-select">
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
            <label className="eyebrow text-[10px] text-neutral-500 block">Timeline note</label><input placeholder="Note for timeline" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputCls} data-testid="order-note-input" />
            <label className="eyebrow text-[10px] text-neutral-500 block">Carrier & tracking number</label>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Carrier" value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} className={inputCls} data-testid="order-carrier-input" />
              <input placeholder="Tracking number" value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} className={inputCls} data-testid="order-tracking-input" />
            </div>
            {edit?.shipping && <p className="text-xs text-neutral-500">Ship to: {[edit.shipping.name, edit.shipping.line1, edit.shipping.city, edit.shipping.state, edit.shipping.postal_code, edit.shipping.country].filter(Boolean).join(", ")}</p>}
            <RecordedSave key={edit?.id} res="orders" id={edit?.id} onSave={save} value={form} label="Save & Notify" testId="order-save-button" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
